import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Check,
  Eraser,
  Minus,
  Palette,
  Pencil,
  PenTool,
  Plus,
  Search,
  type LucideIcon,
  Type,
  X
} from "lucide-react";
import {
  type CSSProperties,
  type FocusEvent as ReactFocusEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

export type SignatureStudioMode = "draw" | "type";

interface SignatureStudioModalProps {
  initialName: string;
  mode: SignatureStudioMode | null;
  onClose: () => void;
  onSave: (result: SignatureStudioSaveResult) => void;
}

export interface SignatureStudioSaveResult {
  dataUrl: string;
  method: SignatureStudioMode;
  signerName?: string;
}

interface SignatureFontOption {
  family: string;
  id: string;
  label: string;
  loadFamily: string;
}

interface DrawToolOption {
  hint: string;
  icon: LucideIcon;
  id: string;
  label: string;
  mode: "draw" | "erase";
  opacity: number;
  strokeWidth: number;
}

interface DrawColorOption {
  id: string;
  label: string;
  value: string;
}

type DrawDesktopPanelId = "colors" | "tools";

interface DrawBounds {
  maxX: number;
  maxY: number;
  minX: number;
  minY: number;
}

const DRAW_TOOL_OPTIONS: DrawToolOption[] = [
  {
    id: "pencil",
    label: "Pencil",
    hint: "Light, refined strokes for sketching the signature shape naturally.",
    icon: Pencil,
    mode: "draw",
    strokeWidth: 1.8,
    opacity: 0.72
  },
  {
    id: "pen",
    label: "Pen",
    hint: "Balanced ink flow for clean, confident signatures that stay legible.",
    icon: PenTool,
    mode: "draw",
    strokeWidth: 3,
    opacity: 1
  },
  {
    id: "brush",
    label: "Brush",
    hint: "Softer ink weight for expressive handwritten signatures.",
    icon: Palette,
    mode: "draw",
    strokeWidth: 5.6,
    opacity: 0.84
  },
  {
    id: "eraser",
    label: "Eraser",
    hint: "Clean up only the parts you want to remove without resetting the entire canvas.",
    icon: Eraser,
    mode: "erase",
    strokeWidth: 22,
    opacity: 1
  }
];

const DRAW_COLOR_OPTIONS: DrawColorOption[] = [
  { id: "midnight", label: "Midnight blue", value: "#13244d" },
  { id: "black", label: "Ink black", value: "#0f172a" },
  { id: "royal", label: "Royal blue", value: "#1d4ed8" },
  { id: "teal", label: "Deep teal", value: "#0f766e" },
  { id: "slate", label: "Slate", value: "#334155" },
  { id: "espresso", label: "Espresso", value: "#7c2d12" }
];

const TYPE_COLOR_OPTIONS = ["#111827", "#13244d", "#1d4ed8", "#334155"];

const SIGNATURE_FONT_OPTIONS: SignatureFontOption[] = [
  {
    id: "great-vibes",
    label: "Great Vibes",
    family: "\"Great Vibes\", cursive",
    loadFamily: "\"Great Vibes\""
  },
  {
    id: "allura",
    label: "Allura",
    family: "\"Allura\", cursive",
    loadFamily: "\"Allura\""
  },
  {
    id: "dancing-script",
    label: "Dancing Script",
    family: "\"Dancing Script\", cursive",
    loadFamily: "\"Dancing Script\""
  },
  {
    id: "sacramento",
    label: "Sacramento",
    family: "\"Sacramento\", cursive",
    loadFamily: "\"Sacramento\""
  },
  {
    id: "pacifico",
    label: "Pacifico",
    family: "\"Pacifico\", cursive",
    loadFamily: "\"Pacifico\""
  }
];

export function SignatureStudioModal({
  initialName,
  mode,
  onClose,
  onSave
}: SignatureStudioModalProps) {
  const open = Boolean(mode);

  useEffect(() => {
    if (!open) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-[120] overflow-hidden bg-[#eef2ff]"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
        >
          <motion.div
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="flex h-full min-h-0 flex-col overflow-hidden"
            exit={{ opacity: 0, scale: 0.995, y: 8 }}
            initial={{ opacity: 0, scale: 0.995, y: 20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {mode === "draw" ? (
              <DrawSignatureStudio
                onClose={onClose}
                onSave={onSave}
              />
            ) : (
              <TypeSignatureStudio
                initialName={initialName}
                onClose={onClose}
                onSave={onSave}
              />
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function DrawSignatureStudio({
  onClose,
  onSave
}: {
  onClose: () => void;
  onSave: (result: SignatureStudioSaveResult) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const isDrawingRef = useRef(false);
  const boundsRef = useRef<DrawBounds | null>(null);
  const [selectedColor, setSelectedColor] = useState(DRAW_COLOR_OPTIONS[0].value);
  const [selectedToolId, setSelectedToolId] = useState("pen");
  const [hasSignature, setHasSignature] = useState(false);
  const [desktopPanel, setDesktopPanel] = useState<DrawDesktopPanelId | null>(null);
  const [hint, setHint] = useState(
    "Use the full canvas to sign naturally with your mouse, trackpad, or finger."
  );

  const selectedTool =
    DRAW_TOOL_OPTIONS.find((tool) => tool.id === selectedToolId) ?? DRAW_TOOL_OPTIONS[0];
  const selectedColorOption =
    DRAW_COLOR_OPTIONS.find((color) => color.value === selectedColor) ?? DRAW_COLOR_OPTIONS[0];
  const SelectedToolIcon = selectedTool.icon;
  const canvasCursor = useMemo(
    () => buildDrawCursor(selectedTool, selectedColor),
    [selectedColor, selectedTool.id]
  );
  const activeToolBadgeClass =
    selectedTool.mode === "erase"
      ? "bg-[#fff1f2] text-[#e11d48]"
      : "bg-[#eef2ff] text-[#1d4ed8]";

  useEffect(() => {
    const snapshot =
      hasSignature && canvasRef.current ? canvasRef.current.toDataURL("image/png") : undefined;

    syncCanvas({
      canvasRef,
      frameRef,
      selectedColor,
      selectedTool,
      snapshot
    });
  }, [selectedColor, selectedTool.id]);

  useEffect(() => {
    const handleResize = () => {
      const snapshot = hasSignature ? canvasRef.current?.toDataURL("image/png") : undefined;

      syncCanvas({
        canvasRef,
        frameRef,
        selectedColor,
        selectedTool,
        snapshot
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [hasSignature, selectedColor, selectedTool]);

  function getPoint(event: ReactPointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;

    if (!canvas) {
      return { x: 0, y: 0 };
    }

    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }

  function updateBounds(x: number, y: number) {
    boundsRef.current = boundsRef.current
      ? {
          minX: Math.min(boundsRef.current.minX, x),
          minY: Math.min(boundsRef.current.minY, y),
          maxX: Math.max(boundsRef.current.maxX, x),
          maxY: Math.max(boundsRef.current.maxY, y)
        }
      : {
          minX: x,
          minY: y,
          maxX: x,
          maxY: y
        };
  }

  function beginDraw(event: ReactPointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");

    if (!canvas || !context) {
      return;
    }

    if (selectedTool.mode === "erase" && !hasSignature) {
      setHint("There is nothing on the canvas to erase yet. Switch to Pencil, Pen, or Brush to begin.");
      return;
    }

    event.preventDefault();
    canvas.setPointerCapture(event.pointerId);
    isDrawingRef.current = true;

    applyBrushStyle(context, selectedColor, selectedTool);

    const { x, y } = getPoint(event);
    updateBounds(x, y);
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + 0.2, y + 0.2);
    context.stroke();

    if (selectedTool.mode === "draw") {
      setHasSignature(true);
      setHint(`Drawing with ${selectedTool.label.toLowerCase()}. Release when the signature feels complete, then save.`);
    } else {
      setHint("Erasing in progress. Release when the signature feels clean and balanced.");
    }
  }

  function draw(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!isDrawingRef.current) {
      return;
    }

    const context = canvasRef.current?.getContext("2d");

    if (!context) {
      return;
    }

    event.preventDefault();
    applyBrushStyle(context, selectedColor, selectedTool);

    const nativeEvents =
      typeof event.nativeEvent.getCoalescedEvents === "function"
        ? event.nativeEvent.getCoalescedEvents()
        : [event.nativeEvent];

    const rect = event.currentTarget.getBoundingClientRect();

    for (const nativeEvent of nativeEvents) {
      const x = nativeEvent.clientX - rect.left;
      const y = nativeEvent.clientY - rect.top;
      updateBounds(x, y);
      context.lineTo(x, y);
      context.stroke();
    }
  }

  function endDraw(event?: ReactPointerEvent<HTMLCanvasElement>) {
    if (!isDrawingRef.current) {
      return;
    }

    isDrawingRef.current = false;

    if (event) {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }

    canvasRef.current?.getContext("2d")?.closePath();
    setHint(
      selectedTool.mode === "erase"
        ? "Erasing complete. Continue refining or switch back to an ink tool before saving."
        : "Signature captured. Review it on the canvas, then save when you are ready."
    );
  }

  function clearCanvas() {
    boundsRef.current = null;
    setHasSignature(false);
    setHint("Canvas cleared. Draw again with plenty of room across the page.");
    syncCanvas({
      canvasRef,
      frameRef,
      selectedColor,
      selectedTool
    });
  }

  function handleSave() {
    const canvas = canvasRef.current;

    if (!canvas || !hasSignature) {
      return;
    }

    const signatureDataUrl = exportTrimmedCanvas(canvas, boundsRef.current);

    onSave({
      dataUrl: signatureDataUrl,
      method: "draw"
    });
  }

  return (
    <>
      <StudioHeader
        accent="draw"
        onClose={onClose}
        onSave={handleSave}
        saveDisabled={!hasSignature}
        secondaryAction={{
          disabled: !hasSignature,
          icon: Eraser,
          label: "Clear",
          onClick: clearCanvas
        }}
        subtitle="Full-screen signature canvas"
        title="Draw your signature"
      />

      <div className="min-h-0 flex-1 overflow-hidden bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.12),transparent_24%),radial-gradient(circle_at_bottom_right,rgba(15,118,110,0.12),transparent_26%),linear-gradient(180deg,#eef4ff_0%,#f8fbff_48%,#f1f5f9_100%)]">
        <div className="grid h-full min-h-0 lg:grid-cols-[92px_minmax(0,1fr)]">
          <aside className="relative hidden border-r border-[#dbe4f3] bg-[linear-gradient(180deg,#eef2ff_0%,#e6ecf8_100%)] lg:flex lg:flex-col lg:items-center lg:justify-start lg:px-3 lg:py-6">
            <div className="w-full space-y-3 overflow-visible">
              <DrawDesktopRailItem
                active={desktopPanel === "tools"}
                icon={PenTool}
                label="Tools"
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    setDesktopPanel((current) => (current === "tools" ? null : current));
                  }
                }}
                onMouseEnter={() => setDesktopPanel("tools")}
                onMouseLeave={() => setDesktopPanel((current) => (current === "tools" ? null : current))}
                panelAlign="top"
                panel={
                  <div className="w-[212px] max-h-[calc(100vh-170px)] overflow-y-auto rounded-[22px] border border-[#d7e1f3] bg-[linear-gradient(180deg,#f6f9ff_0%,#eaf0fb_100%)] p-2.5 text-[#334155] shadow-[0_24px_70px_rgba(148,163,184,0.22)]">
                    <div className="px-1">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                        Tools
                      </p>
                    </div>

                    <div className="mt-2.5 space-y-1">
                      {DRAW_TOOL_OPTIONS.map((tool) => (
                        <DrawDesktopToolOption
                          active={tool.id === selectedTool.id}
                          key={tool.id}
                          onClick={() => setSelectedToolId(tool.id)}
                          tool={tool}
                        />
                      ))}
                    </div>
                  </div>
                }
              />

              <DrawDesktopRailItem
                active={desktopPanel === "colors"}
                icon={Palette}
                label="Colors"
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                    setDesktopPanel((current) => (current === "colors" ? null : current));
                  }
                }}
                onMouseEnter={() => setDesktopPanel("colors")}
                onMouseLeave={() => setDesktopPanel((current) => (current === "colors" ? null : current))}
                panelAlign="bottom"
                panel={
                  <div className="w-[204px] max-h-[calc(100vh-170px)] overflow-y-auto rounded-[22px] border border-[#d7e1f3] bg-[linear-gradient(180deg,#f6f9ff_0%,#eaf0fb_100%)] p-2.5 text-[#334155] shadow-[0_24px_70px_rgba(148,163,184,0.22)]">
                    <div className="px-1">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                        Colors
                      </p>
                    </div>

                    <div className="mt-2.5 grid gap-1">
                      {DRAW_COLOR_OPTIONS.map((color) => (
                        <DrawDesktopColorOption
                          active={selectedColor === color.value}
                          color={color}
                          key={color.id}
                          onClick={() => setSelectedColor(color.value)}
                        />
                      ))}
                    </div>
                  </div>
                }
              />
            </div>
          </aside>

          <div className="min-h-0 flex flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain lg:overflow-hidden">
              <div className="px-3 py-3 sm:px-4 sm:py-4 md:px-6 md:py-6 lg:h-full lg:px-8 lg:py-8">
                <div className="flex min-h-full flex-col lg:h-full">
                  <div className="mb-4 hidden flex-wrap items-center justify-between gap-3 md:flex">
                    <div className="flex flex-wrap items-center gap-2">
                      <div
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] ${activeToolBadgeClass}`}
                      >
                        <SelectedToolIcon className="h-3.5 w-3.5" />
                        {selectedTool.label}
                      </div>
                      <div className="inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/80 px-3 py-2 text-xs font-medium text-slate shadow-sm backdrop-blur">
                        <span
                          className="h-3 w-3 rounded-full border border-slate-200"
                          style={{ backgroundColor: selectedColor }}
                        />
                        {selectedColorOption.label}
                      </div>
                    </div>

                    <div className="max-w-2xl rounded-full border border-white/80 bg-white/80 px-4 py-2 text-xs font-medium text-slate shadow-sm backdrop-blur">
                      {hint}
                    </div>
                  </div>

                  <div className="relative overflow-hidden rounded-[28px] border border-white/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.92)_0%,rgba(248,250,252,0.96)_100%)] shadow-[0_26px_70px_rgba(15,23,42,0.12)] sm:rounded-[32px] lg:min-h-0 lg:flex-1 lg:rounded-[34px] lg:shadow-[0_34px_90px_rgba(15,23,42,0.14)]">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(59,130,246,0.08)_0%,rgba(255,255,255,0)_62%)]" />
                    <div className="absolute inset-0 bg-[radial-gradient(#d7dfeb_1px,transparent_1px)] [background-size:22px_22px] opacity-55" />


                    <div className="absolute inset-x-4 bottom-14 h-px bg-gradient-to-r from-transparent via-[#13244d]/28 to-transparent sm:inset-x-10 sm:bottom-16" />
                    <div className="absolute inset-x-0 bottom-9 flex justify-center sm:bottom-11">
                      <span className="rounded-full border border-white/80 bg-white/88 px-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate/70 shadow-sm backdrop-blur">
                        Sign here
                      </span>
                    </div>

                    <div
                      className="relative flex h-[58vh] min-h-[360px] flex-1 sm:h-[60vh] sm:min-h-[400px] md:min-h-[420px] lg:h-full lg:min-h-0"
                      ref={frameRef}
                    >
                      <canvas
                        aria-label="Signature canvas"
                        className={`block h-full w-full touch-none ${
                          selectedTool.mode === "erase" ? "cursor-cell" : "cursor-crosshair"
                        }`}
                        onPointerCancel={endDraw}
                        onPointerDown={beginDraw}
                        onPointerLeave={endDraw}
                        onPointerMove={draw}
                        onPointerUp={endDraw}
                        ref={canvasRef}
                        style={{ cursor: canvasCursor, touchAction: "none" }}
                      />
                    </div>
                  </div>

                  <div className="mt-4 lg:hidden">
                    <DrawMobileToolbar
                      onClear={clearCanvas}
                      onColorChange={setSelectedColor}
                      onToolChange={setSelectedToolId}
                      selectedColor={selectedColor}
                      selectedColorLabel={selectedColorOption.label}
                      selectedToolId={selectedTool.id}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function TypeSignatureStudio({
  initialName,
  onClose,
  onSave
}: {
  initialName: string;
  onClose: () => void;
  onSave: (result: SignatureStudioSaveResult) => void;
}) {
  const [fontQuery, setFontQuery] = useState("");
  const [fontSize, setFontSize] = useState(88);
  const [selectedColor, setSelectedColor] = useState(TYPE_COLOR_OPTIONS[0]);
  const [selectedFontId, setSelectedFontId] = useState(SIGNATURE_FONT_OPTIONS[0].id);
  const [typedName, setTypedName] = useState(initialName);

  const selectedFont =
    SIGNATURE_FONT_OPTIONS.find((font) => font.id === selectedFontId) ?? SIGNATURE_FONT_OPTIONS[0];

  const filteredFonts = useMemo(() => {
    const query = fontQuery.trim().toLowerCase();

    if (!query) {
      return SIGNATURE_FONT_OPTIONS;
    }

    return SIGNATURE_FONT_OPTIONS.filter((font) =>
      font.label.toLowerCase().includes(query)
    );
  }, [fontQuery]);

  const previewStyle = useMemo<CSSProperties>(
    () => ({
      color: selectedColor,
      fontFamily: selectedFont.family,
      fontSize: `clamp(3rem, ${Math.max(fontSize / 32, 2.6)}rem, ${Math.max(
        fontSize / 14,
        5.4
      )}rem)`,
      lineHeight: 1.1
    }),
    [fontSize, selectedColor, selectedFont.family]
  );

  async function handleSave() {
    const cleanName = typedName.trim();

    if (!cleanName) {
      return;
    }

    const dataUrl = await renderTypedSignatureToDataUrl({
      color: selectedColor,
      font: selectedFont,
      fontSize,
      text: cleanName
    });

    onSave({
      dataUrl,
      method: "type",
      signerName: cleanName
    });
  }

  return (
    <>
      <StudioHeader
        accent="type"
        onClose={onClose}
        onSave={handleSave}
        saveDisabled={!typedName.trim()}
        subtitle="Full-screen signature typography"
        title="Type your signature"
      />

      <div className="grid min-h-0 flex-1 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="hidden min-h-0 border-r border-line/70 bg-white/92 lg:flex lg:flex-col">
          <div className="border-b border-line/70 px-6 py-6">
            <p className="eyebrow">Font Library</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
              Signature-style fonts
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate">
              Enter the signer’s name, then choose a script face that feels polished and legible.
            </p>
          </div>

          <div className="space-y-4 px-6 py-5">
            <label className="block space-y-2">
              <span className="field-label">Signer name</span>
              <input
                className="input-field"
                onChange={(event) => setTypedName(event.target.value)}
                placeholder="Enter the full name"
                value={typedName}
              />
            </label>

            <label className="block space-y-2">
              <span className="field-label">Search fonts</span>
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate/65" />
                <input
                  className="input-field pl-11"
                  onChange={(event) => setFontQuery(event.target.value)}
                  placeholder="Search script fonts"
                  value={fontQuery}
                />
              </div>
            </label>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-5">
            <div className="space-y-2">
              {filteredFonts.map((font) => {
                const active = font.id === selectedFont.id;

                return (
                  <button
                    className={`flex w-full items-center justify-between rounded-[24px] border px-4 py-4 text-left transition ${
                      active
                        ? "border-[#8b5cf6]/25 bg-[#f5f3ff] shadow-sm"
                        : "border-transparent bg-transparent hover:bg-slate-50"
                    }`}
                    key={font.id}
                    onClick={() => setSelectedFontId(font.id)}
                    type="button"
                  >
                    <span>
                      <span className="block text-sm font-semibold text-ink">{font.label}</span>
                      <span
                        className="mt-2 block text-[1.95rem] text-slate-900"
                        style={{ fontFamily: font.family, lineHeight: 1.05 }}
                      >
                        {typedName.trim() || "Type your signature"}
                      </span>
                    </span>
                    {active ? (
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#7c3aed] shadow-sm">
                        <Check className="h-4 w-4" />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        <div className="min-h-0 bg-[#f4f7fb]">
          <div className="grid h-full min-h-0 grid-rows-[auto_1fr_auto] gap-4 px-4 py-4 md:px-6 lg:px-8 lg:py-6">
            <div className="overflow-hidden rounded-[28px] border border-line/70 bg-white/92 shadow-sm">
              <div className="flex flex-wrap items-center gap-3 px-4 py-4 lg:px-5">
                <div className="inline-flex items-center gap-3 rounded-[18px] border border-line/70 bg-white px-3 py-2 shadow-sm">
                  <span className="text-sm font-semibold text-ink">{selectedFont.label}</span>
                </div>

                <div className="inline-flex items-center gap-2 rounded-[18px] border border-line/70 bg-white px-3 py-2 shadow-sm">
                  <button
                    className="rounded-lg p-1 text-slate transition hover:bg-cloud"
                    onClick={() => setFontSize((current) => Math.max(current - 6, 56))}
                    type="button"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="min-w-10 text-center text-sm font-semibold text-ink">
                    {fontSize}
                  </span>
                  <button
                    className="rounded-lg p-1 text-slate transition hover:bg-cloud"
                    onClick={() => setFontSize((current) => Math.min(current + 6, 132))}
                    type="button"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                <div className="inline-flex items-center gap-2 rounded-[18px] border border-line/70 bg-white px-3 py-2 shadow-sm">
                  <Palette className="h-4 w-4 text-slate" />
                  <div className="flex gap-2">
                    {TYPE_COLOR_OPTIONS.map((color) => (
                      <button
                        aria-label={`Select ${color} ink`}
                        className={`h-7 w-7 rounded-full border-2 transition ${
                          selectedColor === color
                            ? "scale-105 border-white shadow-[0_0_0_3px_rgba(124,58,237,0.18)]"
                            : "border-white/70 shadow-sm"
                        }`}
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        style={{ backgroundColor: color }}
                        type="button"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="min-h-0 overflow-hidden rounded-[36px] border border-line/70 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.1)]">
              <div className="h-full min-h-0 overflow-y-auto bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] p-4 md:p-6 lg:p-8">
                <div className="lg:hidden">
                  <TypeMobileControls
                    fontQuery={fontQuery}
                    fontSize={fontSize}
                    onFontQueryChange={setFontQuery}
                    onFontSizeChange={setFontSize}
                    onNameChange={setTypedName}
                    selectedColor={selectedColor}
                    selectedFont={selectedFont}
                    selectedFontId={selectedFont.id}
                    selectedColorChange={setSelectedColor}
                    selectedFontIdChange={setSelectedFontId}
                    typedName={typedName}
                  />
                </div>

                <div className="flex h-full min-h-[420px] items-center justify-center">
                  <div className="w-full max-w-[1180px]">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-2 rounded-full bg-[#f5f3ff] px-3 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#7c3aed]">
                        <Type className="h-3.5 w-3.5" />
                        Live preview
                      </div>
                      <div className="rounded-full bg-white/90 px-3 py-2 text-xs font-medium text-slate shadow-sm">
                        Choose a script font and save the signature back into the agreement
                      </div>
                    </div>

                    <div className="relative overflow-hidden rounded-[34px] border border-[#ddd6fe] bg-white px-4 py-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] md:px-8 md:py-10">
                      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(168,85,247,0.08),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.08),transparent_36%)]" />
                      <div className="pointer-events-none absolute inset-x-6 top-6 hidden items-center justify-between md:flex">
                        <SelectionHandle align="left" />
                        <SelectionHandle align="right" />
                      </div>
                      <div className="pointer-events-none absolute inset-x-6 bottom-6 hidden items-center justify-between md:flex">
                        <SelectionHandle align="left" />
                        <SelectionHandle align="right" />
                      </div>

                      <div className="relative rounded-[26px] border border-[#d8b4fe]/55 bg-white px-4 py-10 md:px-8 md:py-12">
                        <div className="pointer-events-none absolute inset-x-8 bottom-16 h-px bg-gradient-to-r from-transparent via-[#13244d]/20 to-transparent" />
                        <div className="pointer-events-none absolute inset-x-0 bottom-11 flex justify-center">
                          <span className="rounded-full bg-white px-4 text-[11px] font-semibold uppercase tracking-[0.24em] text-slate/65">
                            Sign here
                          </span>
                        </div>

                        <div className="relative flex min-h-[240px] items-center justify-center text-center md:min-h-[300px]">
                          <span style={previewStyle}>
                            {typedName.trim() || "Type your signature here"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* <div className="grid gap-3 rounded-[28px] border border-line/70 bg-white/85 px-5 py-4 shadow-sm md:grid-cols-3">
              <StudioFooterCard
                title="Name entry"
                value="Users can update the signer name inside the typing studio and it saves back into the form."
              />
              <StudioFooterCard
                title="Script fonts"
                value="Several polished signature fonts are available so the result feels intentional instead of default."
              />
              <StudioFooterCard
                title="Responsive layout"
                value="On smaller screens the font controls move into the content flow to keep the full-screen workspace comfortable."
              />
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
}

function StudioHeader({
  accent,
  onClose,
  onSave,
  saveDisabled,
  secondaryAction,
  subtitle,
  title
}: {
  accent: SignatureStudioMode;
  onClose: () => void;
  onSave: () => void;
  saveDisabled: boolean;
  secondaryAction?: {
    disabled?: boolean;
    icon: LucideIcon;
    label: string;
    onClick: () => void;
  };
  subtitle: string;
  title: string;
}) {
  return (
    <header
      className={`shrink-0 border-b border-white/10 px-4 py-3 text-white sm:px-5 md:px-6 ${
        accent === "draw"
          ? "bg-gradient-to-r from-[#14b8a6] via-[#2f6de3] to-[#6d28d9]"
          : "bg-gradient-to-r from-[#0ea5e9] via-[#4f46e5] to-[#7c3aed]"
      }`}
    >
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-3 sm:items-center">
          <button
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/10 transition hover:bg-white/16 sm:h-11 sm:w-11"
            onClick={onClose}
            type="button"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>

          <div className="min-w-0">
            <p className="hidden text-[11px] font-semibold uppercase tracking-[0.24em] text-white/75 sm:block">
              {subtitle}
            </p>
            <h1 className="text-xl font-semibold tracking-tight text-white sm:mt-1 sm:text-2xl">
              {title}
            </h1>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {secondaryAction ? (
            <button
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-white/16 disabled:cursor-not-allowed disabled:opacity-55 sm:flex-none sm:px-4 sm:py-3"
              disabled={secondaryAction.disabled}
              onClick={secondaryAction.onClick}
              type="button"
            >
              <secondaryAction.icon className="h-4 w-4" />
              {secondaryAction.label}
            </button>
          ) : null}

          <button
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-3 py-2.5 text-sm font-semibold text-white transition hover:bg-white/16 sm:flex-none sm:px-4 sm:py-3"
            onClick={onClose}
            type="button"
          >
            <X className="h-4 w-4" />
            Cancel
          </button>
          <button
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-5 py-2.5 text-sm font-semibold text-ink shadow-sm transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 sm:w-auto sm:px-5 sm:py-3"
            disabled={saveDisabled}
            onClick={onSave}
            type="button"
          >
            <Check className="h-4 w-4" />
            Save signature
          </button>
        </div>
      </div>
    </header>
  );
}

function DrawDesktopRailItem({
  active,
  icon: Icon,
  label,
  onBlur,
  onMouseEnter,
  onMouseLeave,
  panelAlign,
  panel
}: {
  active: boolean;
  icon: LucideIcon;
  label: string;
  onBlur: (event: ReactFocusEvent<HTMLDivElement>) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  panelAlign: "bottom" | "top";
  panel: React.ReactNode;
}) {
  return (
    <div
      className="group relative flex justify-center overflow-visible"
      onBlur={onBlur}
      onFocusCapture={onMouseEnter}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        aria-expanded={active}
        aria-haspopup="menu"
        className={`flex w-full flex-col items-center gap-2 rounded-[24px] px-2 py-4 text-center transition ${
          active
            ? "bg-[linear-gradient(180deg,#f7f9ff_0%,#edf2ff_100%)] text-[#2f5eff] shadow-[0_16px_36px_rgba(148,163,184,0.24)]"
            : "text-slate-600 hover:bg-white/70 hover:text-slate-800"
        }`}
        type="button"
      >
        <span
          className={`flex h-11 w-11 items-center justify-center rounded-2xl ${
            active ? "bg-white text-[#2f5eff] shadow-[0_8px_24px_rgba(148,163,184,0.2)]" : "bg-white/75 text-slate-500"
          }`}
        >
          <Icon className="h-4.5 w-4.5" />
        </span>
        <span className="text-[13px] font-semibold tracking-[0.01em] text-current">{label}</span>
        <span
          className={`h-1.5 w-6 rounded-full transition ${
            active ? "bg-[#2f5eff]" : "bg-slate-300 group-hover:bg-slate-400"
          }`}
        />
      </button>

      <AnimatePresence>
        {active ? (
          <motion.div
            animate={{ opacity: 1, x: 0, y: 0 }}
            className={`absolute left-[calc(100%-6px)] z-30 ${
              panelAlign === "top" ? "top-0" : "bottom-0"
            }`}
            exit={{ opacity: 0, x: -10, y: 0 }}
            initial={{ opacity: 0, x: -10, y: 0 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
          >
            {panel}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

function DrawDesktopToolOption({
  active,
  onClick,
  tool
}: {
  active: boolean;
  onClick: () => void;
  tool: DrawToolOption;
}) {
  const Icon = tool.icon;

  return (
    <button
      aria-pressed={active}
      className={`group flex w-full items-center gap-2.5 rounded-[16px] border px-2.5 py-2 text-left transition ${
        active
          ? tool.mode === "erase"
            ? "border-[#fecdd3] bg-[#fff1f2] text-slate-800"
            : "border-[#c7d7ff] bg-[#eef3ff] text-slate-800"
          : "border-transparent bg-transparent text-slate-700 hover:border-slate-200 hover:bg-white/70 hover:text-slate-900"
      }`}
      onClick={onClick}
      type="button"
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl ${
          active
            ? tool.mode === "erase"
              ? "bg-white text-[#e11d48]"
              : "bg-white text-[#2f5eff]"
            : "bg-white/80 text-slate-500"
        }`}
      >
        <Icon className="h-4 w-4" />
      </span>

      <span className="min-w-0 flex-1 truncate text-sm font-semibold">{tool.label}</span>

      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition ${
          active
            ? tool.mode === "erase"
              ? "bg-[#ffe4e6] text-[#e11d48]"
              : "bg-[#dbe7ff] text-[#2f5eff]"
            : "text-slate-300 group-hover:text-slate-500"
        }`}
      >
        <Check className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}

function DrawDesktopColorOption({
  active,
  color,
  onClick
}: {
  active: boolean;
  color: DrawColorOption;
  onClick: () => void;
}) {
  return (
    <button
      aria-label={`Select ${color.label} ink`}
      aria-pressed={active}
      className={`group flex items-center gap-2.5 rounded-[16px] border px-2.5 py-2 text-left transition ${
        active
          ? "border-[#c7d7ff] bg-[#eef3ff] text-slate-800"
          : "border-transparent bg-transparent text-slate-700 hover:border-slate-200 hover:bg-white/70 hover:text-slate-900"
      }`}
      onClick={onClick}
      type="button"
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl ${
          active ? "bg-white shadow-sm" : "bg-white/75"
        }`}
      >
        <span
          className="h-5 w-5 rounded-full border-2 border-white shadow-sm"
          style={{ backgroundColor: color.value }}
        />
      </span>

      <span className="min-w-0 flex-1 truncate text-sm font-semibold">{color.label}</span>

      <span
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition ${
          active ? "bg-[#dbe7ff] text-[#2f5eff]" : "text-slate-300 group-hover:text-slate-500"
        }`}
      >
        <Check className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}

function DrawMobileToolbar({
  onClear,
  onColorChange,
  onToolChange,
  selectedColor,
  selectedColorLabel,
  selectedToolId
}: {
  onClear: () => void;
  onColorChange: (color: string) => void;
  onToolChange: (toolId: string) => void;
  selectedColor: string;
  selectedColorLabel: string;
  selectedToolId: string;
}) {
  const selectedTool =
    DRAW_TOOL_OPTIONS.find((tool) => tool.id === selectedToolId) ?? DRAW_TOOL_OPTIONS[0];

  return (
    <div className="rounded-[24px] border border-line/70 bg-white/92 p-3 shadow-sm sm:rounded-[26px] sm:p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/65">
            Tools
          </p>
          <p className="mt-1 text-xs leading-5 text-slate">
            Tap a tool, then sign directly on the canvas below.
          </p>
        </div>
        <button className="btn-ghost shrink-0 px-3 py-2" onClick={onClear} type="button">
          <Eraser className="h-4 w-4" />
          Clear
        </button>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2">
        {DRAW_TOOL_OPTIONS.map((tool) => {
          const active = tool.id === selectedToolId;

          return (
            <button
              aria-pressed={active}
              className={`flex min-h-[78px] flex-col items-center justify-center gap-2 rounded-[18px] border px-2 py-3 text-center transition ${
                active
                  ? tool.mode === "erase"
                    ? "border-[#fecaca] bg-[#fff1f2]"
                    : "border-[#bfdbfe] bg-[#eff6ff]"
                  : "border-line/70 bg-white"
              }`}
              key={tool.id}
              onClick={() => onToolChange(tool.id)}
              type="button"
            >
              <span
                className={`flex h-9 w-9 items-center justify-center rounded-2xl ${
                  active
                    ? tool.mode === "erase"
                      ? "bg-white text-[#e11d48]"
                      : "bg-white text-[#1d4ed8]"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                <tool.icon className="h-4 w-4" />
              </span>
              <span className="text-[11px] font-semibold leading-4 text-ink">{tool.label}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/65">
          Ink colors
        </p>
        <span
          className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] ${
            selectedTool.mode === "erase"
              ? "bg-[#fff1f2] text-[#e11d48]"
              : "bg-[#eef2ff] text-[#1d4ed8]"
          }`}
        >
          {selectedTool.label}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {DRAW_COLOR_OPTIONS.map((color) => (
          <button
            aria-label={`Select ${color.label} ink`}
            className={`h-10 w-10 rounded-full border-2 transition ${
              selectedColor === color.value
                ? "scale-105 border-white shadow-[0_0_0_3px_rgba(37,99,235,0.18)]"
                : "border-white/70 shadow-sm"
            }`}
            key={color.id}
            onClick={() => onColorChange(color.value)}
            style={{ backgroundColor: color.value }}
            type="button"
          />
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 rounded-[18px] border border-line/70 bg-slate-50 px-3 py-2.5">
        <span className="text-xs font-medium text-slate">
          {selectedTool.mode === "erase"
            ? "Switch back to an ink tool when you are done cleaning up."
            : `Current ink: ${selectedColorLabel}.`}
        </span>
      </div>
    </div>
  );
}

function TypeMobileControls({
  fontQuery,
  fontSize,
  onFontQueryChange,
  onFontSizeChange,
  onNameChange,
  selectedColor,
  selectedColorChange,
  selectedFont,
  selectedFontId,
  selectedFontIdChange,
  typedName
}: {
  fontQuery: string;
  fontSize: number;
  onFontQueryChange: (value: string) => void;
  onFontSizeChange: (value: number) => void;
  onNameChange: (value: string) => void;
  selectedColor: string;
  selectedColorChange: (value: string) => void;
  selectedFont: SignatureFontOption;
  selectedFontId: string;
  selectedFontIdChange: (value: string) => void;
  typedName: string;
}) {
  const filteredFonts = useMemo(() => {
    const query = fontQuery.trim().toLowerCase();

    if (!query) {
      return SIGNATURE_FONT_OPTIONS;
    }

    return SIGNATURE_FONT_OPTIONS.filter((font) =>
      font.label.toLowerCase().includes(query)
    );
  }, [fontQuery]);

  return (
    <div className="mb-4 space-y-4 rounded-[26px] border border-line/70 bg-white/90 p-4 shadow-sm lg:hidden">
      <label className="block space-y-2">
        <span className="field-label">Signer name</span>
        <input
          className="input-field"
          onChange={(event) => onNameChange(event.target.value)}
          placeholder="Enter the full name"
          value={typedName}
        />
      </label>

      <label className="block space-y-2">
        <span className="field-label">Search fonts</span>
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate/65" />
          <input
            className="input-field pl-11"
            onChange={(event) => onFontQueryChange(event.target.value)}
            placeholder="Search script fonts"
            value={fontQuery}
          />
        </div>
      </label>

      <div className="flex items-center justify-between gap-3 rounded-[18px] border border-line/70 bg-slate-50 px-3 py-3">
        <span className="text-sm font-semibold text-ink">{selectedFont.label}</span>
        <div className="inline-flex items-center gap-2">
          <button
            className="rounded-lg p-1 text-slate transition hover:bg-white"
            onClick={() => onFontSizeChange(Math.max(fontSize - 6, 56))}
            type="button"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="min-w-9 text-center text-sm font-semibold text-ink">{fontSize}</span>
          <button
            className="rounded-lg p-1 text-slate transition hover:bg-white"
            onClick={() => onFontSizeChange(Math.min(fontSize + 6, 132))}
            type="button"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {filteredFonts.map((font) => (
          <button
            className={`shrink-0 rounded-[20px] border px-4 py-3 text-left transition ${
              font.id === selectedFontId
                ? "border-[#8b5cf6]/25 bg-[#f5f3ff]"
                : "border-line/70 bg-white"
            }`}
            key={font.id}
            onClick={() => selectedFontIdChange(font.id)}
            type="button"
          >
            <span className="block text-xs font-semibold text-ink">{font.label}</span>
            <span
              className="mt-2 block text-[1.65rem] text-slate-900"
              style={{ fontFamily: font.family, lineHeight: 1.05 }}
            >
              {typedName.trim() || "Signature"}
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {TYPE_COLOR_OPTIONS.map((color) => (
          <button
            aria-label={`Select ${color} ink`}
            className={`h-9 w-9 rounded-full border-2 transition ${
              selectedColor === color
                ? "scale-105 border-white shadow-[0_0_0_3px_rgba(124,58,237,0.18)]"
                : "border-white/70 shadow-sm"
            }`}
            key={color}
            onClick={() => selectedColorChange(color)}
            style={{ backgroundColor: color }}
            type="button"
          />
        ))}
      </div>
    </div>
  );
}

function SelectionHandle({ align }: { align: "left" | "right" }) {
  return (
    <span
      className={`flex h-7 w-7 items-center justify-center rounded-full border-2 border-[#c084fc] bg-white shadow-sm ${
        align === "left" ? "-translate-x-1/2" : "translate-x-1/2"
      }`}
    >
      <span className="h-2.5 w-2.5 rounded-full bg-[#a855f7]" />
    </span>
  );
}

function buildDrawCursor(tool: DrawToolOption, color: string) {
  const stroke = tool.mode === "erase" ? "#e11d48" : color;
  const outline = "#0f172a";
  const paper = "#ffffff";

  if (tool.id === "pencil") {
    return buildCursorDataUrl(
      `
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
          <path
            d="M24.6 6.6L29.4 11.4L12.1 28.7L6.2 29.8L7.3 23.9L24.6 6.6Z"
            fill="#fde68a"
            stroke="${outline}"
            stroke-linejoin="round"
            stroke-width="1.5"
          />
          <path d="M22.3 8.9L27.1 13.7" stroke="#ef4444" stroke-linecap="round" stroke-width="2.1" />
          <path
            d="M6.2 29.8L7.3 23.9L12.1 28.7Z"
            fill="#d4a373"
            stroke="${outline}"
            stroke-linejoin="round"
            stroke-width="1.5"
          />
          <path d="M7.4 28.5L9.3 26.6L10.7 28L8.8 29.9Z" fill="${outline}" />
          <circle cx="26.9" cy="9.1" r="1.4" fill="${stroke}" />
        </svg>
      `,
      8,
      29
    );
  }

  if (tool.id === "pen") {
    return buildCursorDataUrl(
      `
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
          <path
            d="M25.5 6.6L29.2 10.3L14.4 25.1L9.7 20.4L25.5 6.6Z"
            fill="${paper}"
            stroke="${outline}"
            stroke-linejoin="round"
            stroke-width="1.5"
          />
          <path d="M23.7 8.4L27.4 12.1" stroke="${stroke}" stroke-linecap="round" stroke-width="2.1" />
          <path
            d="M9.7 20.4L14.4 25.1L7 28.8L5.9 27.7Z"
            fill="${stroke}"
            stroke="${outline}"
            stroke-linejoin="round"
            stroke-width="1.5"
          />
          <path d="M12.6 21.9L15.1 24.4" stroke="${outline}" stroke-linecap="round" stroke-width="1.2" />
        </svg>
      `,
      7,
      28
    );
  }

  if (tool.id === "brush") {
    return buildCursorDataUrl(
      `
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
          <path
            d="M24.7 6.8L29.2 11.3L20.1 20.4L15.6 15.9Z"
            fill="#b45309"
            stroke="${outline}"
            stroke-linejoin="round"
            stroke-width="1.5"
          />
          <path
            d="M15.6 15.9L20.1 20.4L15.4 25.1L10.9 20.6Z"
            fill="#cbd5e1"
            stroke="${outline}"
            stroke-linejoin="round"
            stroke-width="1.5"
          />
          <path
            d="M10.8 20.5L15.5 25.2L11.2 30C9.7 31.6 7.1 31.6 5.5 30C3.9 28.4 3.9 25.8 5.5 24.2L10.8 20.5Z"
            fill="${stroke}"
            stroke="${outline}"
            stroke-linejoin="round"
            stroke-width="1.5"
          />
        </svg>
      `,
      8,
      29
    );
  }

  return buildCursorDataUrl(
    `
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
        <path
          d="M19.4 7.8L27.2 15.6L14.8 28L7 20.2Z"
          fill="#fecdd3"
          stroke="${outline}"
          stroke-linejoin="round"
          stroke-width="1.5"
        />
        <path d="M19.4 7.8L27.2 15.6" stroke="#e11d48" stroke-linecap="round" stroke-width="2.1" />
        <path d="M10.6 23.8L14.8 28" stroke="${paper}" stroke-linecap="round" stroke-width="2.2" />
        <path d="M7 20.2L10.6 23.8" stroke="#e11d48" stroke-linecap="round" stroke-width="2.2" />
      </svg>
    `,
    11,
    24
  );
}

function buildCursorDataUrl(svg: string, hotspotX: number, hotspotY: number) {
  const normalizedSvg = svg.replace(/\s+/g, " ").trim();
  return `url("data:image/svg+xml,${encodeURIComponent(normalizedSvg)}") ${hotspotX} ${hotspotY}, auto`;
}

function applyBrushStyle(
  context: CanvasRenderingContext2D,
  color: string,
  tool: DrawToolOption
) {
  context.lineCap = "round";
  context.lineJoin = "round";
  context.lineWidth = tool.strokeWidth;
  context.globalAlpha = tool.opacity;
  context.globalCompositeOperation = tool.mode === "erase" ? "destination-out" : "source-over";
  context.strokeStyle = tool.mode === "erase" ? "rgba(15, 23, 42, 1)" : color;
}

function syncCanvas({
  canvasRef,
  frameRef,
  selectedColor,
  selectedTool,
  snapshot
}: {
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  frameRef: React.MutableRefObject<HTMLDivElement | null>;
  selectedColor: string;
  selectedTool: DrawToolOption;
  snapshot?: string;
}) {
  const canvas = canvasRef.current;
  const frame = frameRef.current;

  if (!canvas || !frame) {
    return;
  }

  const ratio = window.devicePixelRatio || 1;
  const width = frame.clientWidth;
  const height = frame.clientHeight;
  const context = canvas.getContext("2d");

  if (!context) {
    return;
  }

  canvas.width = width * ratio;
  canvas.height = height * ratio;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  context.setTransform(1, 0, 0, 1, 0, 0);
  context.scale(ratio, ratio);
  context.globalCompositeOperation = "source-over";
  context.globalAlpha = 1;
  context.clearRect(0, 0, width, height);

  if (!snapshot) {
    applyBrushStyle(context, selectedColor, selectedTool);
    return;
  }

  const image = new Image();
  image.onload = () => {
    context.globalCompositeOperation = "source-over";
    context.globalAlpha = 1;
    context.clearRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    applyBrushStyle(context, selectedColor, selectedTool);
  };
  image.src = snapshot;
}

function exportTrimmedCanvas(canvas: HTMLCanvasElement, bounds: DrawBounds | null) {
  if (!bounds) {
    return canvas.toDataURL("image/png");
  }

  const ratio = window.devicePixelRatio || 1;
  const padding = 28;
  const left = Math.max(bounds.minX - padding, 0);
  const top = Math.max(bounds.minY - padding, 0);
  const right = Math.min(bounds.maxX + padding, canvas.width / ratio);
  const bottom = Math.min(bounds.maxY + padding, canvas.height / ratio);
  const width = Math.max(right - left, 1);
  const height = Math.max(bottom - top, 1);
  const trimmedCanvas = document.createElement("canvas");

  trimmedCanvas.width = Math.ceil(width * ratio);
  trimmedCanvas.height = Math.ceil(height * ratio);

  const context = trimmedCanvas.getContext("2d");

  if (!context) {
    return canvas.toDataURL("image/png");
  }

  context.drawImage(
    canvas,
    left * ratio,
    top * ratio,
    width * ratio,
    height * ratio,
    0,
    0,
    width * ratio,
    height * ratio
  );

  return trimmedCanvas.toDataURL("image/png");
}

async function renderTypedSignatureToDataUrl({
  color,
  font,
  fontSize,
  text
}: {
  color: string;
  font: SignatureFontOption;
  fontSize: number;
  text: string;
}) {
  const cleanText = text.trim();
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");

  if (!context) {
    return "";
  }

  if ("fonts" in document) {
    await document.fonts.load(`${fontSize}px ${font.loadFamily}`);
  }

  const ratio = window.devicePixelRatio || 1;
  const paddingX = 72;
  const paddingY = 56;
  context.font = `${fontSize}px ${font.family}`;

  const metrics = context.measureText(cleanText);
  const width = Math.max(
    Math.ceil(metrics.width + paddingX * 2),
    Math.ceil(fontSize * 3.2)
  );
  const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.72;
  const descent = metrics.actualBoundingBoxDescent || fontSize * 0.3;
  const height = Math.max(Math.ceil(ascent + descent + paddingY * 2), 180);

  canvas.width = width * ratio;
  canvas.height = height * ratio;

  context.setTransform(1, 0, 0, 1, 0, 0);
  context.scale(ratio, ratio);
  context.clearRect(0, 0, width, height);
  context.font = `${fontSize}px ${font.family}`;
  context.fillStyle = color;
  context.textBaseline = "alphabetic";
  context.fillText(cleanText, paddingX, paddingY + ascent);

  return canvas.toDataURL("image/png");
}
